import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
} from '@mui/material';
import { PictureAsPdf, ArrowBack, TrendingUp, TrendingDown, Remove, History } from '@mui/icons-material';
import { PageHeader, Loading, ErrorMessage, ScoreChip } from './common';
import { apiService } from '../services/api';

const ScoreDiffChip = ({ diff }) => {
  if (diff == null) return <Chip label="N/A" size="small" variant="outlined" />;
  const isExact = Math.abs(diff) < 0.01;
  const isGt = diff > 0.01;
  const color = isExact ? 'success' : isGt ? 'warning' : 'info';
  const icon = isExact ? <Remove fontSize="small" /> : isGt ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />;
  return (
    <Chip
      icon={icon}
      label={isExact ? 'Exact' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}`}
      color={color}
      size="small"
      variant={isExact ? 'filled' : 'outlined'}
    />
  );
};

const CandidateEvaluation = () => {
  const { candidateId } = useParams();
  const [submissionData, setSubmissionData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [tab, setTab] = useState(0);

  const fetchSubmissionData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.submissions.get(candidateId);
      setSubmissionData(response.data);
    } catch (err) {
      console.error('Error fetching submission data:', err);
      setError('Failed to load submission data');
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  const fetchComparisonData = useCallback(async () => {
    try {
      setCompareLoading(true);
      setCompareError(null);
      const response = await apiService.submissions.compare(candidateId);
      setComparisonData(response.data);
    } catch (err) {
      console.error('Error fetching comparison data:', err);
      setCompareError(err.response?.data?.error || 'Failed to load comparison data');
    } finally {
      setCompareLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchSubmissionData();
    fetchComparisonData();
  }, [fetchSubmissionData, fetchComparisonData]);

  const calculateTotalScore = (evaluations) => {
    if (!evaluations || evaluations.length === 0) return 0;
    return evaluations.reduce((total, evaluation) => {
      const score = evaluation.score || 0;
      const isCounterproductive = evaluation.aspect &&
        evaluation.aspect.toString().toUpperCase().includes('COUNTERPRODUCTIVE');
      return isCounterproductive ? total - score : total + score;
    }, 0);
  };

  const handleGenerateReport = async () => {
    if (generatingReport) return;
    setGeneratingReport(true);
    try {
      const candidate_id = submissionData?.decision_state?.candidate_id;
      const tool_id = submissionData?.decision_state?.whole_data?.tool_id || 7;
      const response = await apiService.candidates.generateReport({
        candidate_id,
        tool_id,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `candidate_${candidateId}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating report:', err);
      let errorMessage = 'Failed to generate report. Please try again.';
      if (err.response?.data?.error) {
        errorMessage = `Error: ${err.response.data.error}`;
      } else if (err.response?.status === 404) {
        errorMessage = 'No evaluation data found for this candidate.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid request parameters.';
      }
      alert(errorMessage);
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return <Loading message={`Loading evaluation data for Submission ${candidateId}...`} />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Error loading evaluation data"
        message={error}
        onRetry={() => { setError(null); fetchSubmissionData(); }}
      />
    );
  }

  const evaluations = submissionData?.evaluations || [];
  const totalScore = calculateTotalScore(evaluations);
  const candidateName = submissionData?.decision_state?.general_info?.name || `Candidate ${submissionData?.decision_state?.candidate_id}`;

  const overviewStats = [
    { label: 'Submission ID', value: submissionData?.submission_id || candidateId },
    { label: 'Candidate ID', value: submissionData?.decision_state?.candidate_id || 'N/A' },
    { label: 'Tool', value: submissionData?.decision_state?.whole_data?.tool_id || 'N/A' },
    { label: 'Evaluations', value: evaluations.length },
  ];

  return (
    <Box>
      <PageHeader
        title={`${candidateName}`}
        subtitle={`Submission ${candidateId} — Detailed evaluation results and score comparison`}
        breadcrumbs={[
          { label: 'Candidates', path: '/candidates' },
          { label: `Submission ${candidateId}`, path: `/candidate/${candidateId}` },
        ]}
        actions={[
          {
            label: generatingReport ? 'Generating...' : 'Generate PDF',
            icon: <PictureAsPdf />,
            onClick: handleGenerateReport,
            loading: generatingReport,
            disabled: generatingReport,
          },
          ...(submissionData?.decision_state?.candidate_id ? [{
            label: 'All Runs',
            icon: <History />,
            component: Link,
            to: `/candidate-detail/${submissionData.decision_state.candidate_id}`,
            variant: 'outlined',
            color: 'secondary',
          }] : []),
          {
            label: 'Back',
            icon: <ArrowBack />,
            component: Link,
            to: '/candidates',
            variant: 'outlined',
          },
        ]}
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label={`Evaluations (${evaluations.length})`} />
        <Tab label="Score Comparison" />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Submission Details
                </Typography>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {overviewStats.map((stat) => (
                    <Grid item xs={6} sm={3} key={stat.label}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.default', borderRadius: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {stat.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {stat.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', py: 2, gap: 2 }}>
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Typography variant="overline" color="text.secondary">
                    Evaluated Total
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1, my: 0.5 }}>
                    <Typography variant="h2" fontWeight={800} color="primary.main">
                      {totalScore.toFixed(1)}
                    </Typography>
                    <Typography variant="h5" color="text.secondary">/ 80</Typography>
                  </Box>
                  <ScoreChip score={totalScore} maxScore={80} size="medium" />
                </Box>
                {comparisonData && comparisonData.detailed_differences_comparison?.length > 0 && (
                  <>
                    <Box sx={{ width: '80%', borderTop: 1, borderColor: 'divider' }} />
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                      <Typography variant="overline" color="text.secondary">
                        Ground Truth Total
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1, my: 0.5 }}>
                        <Typography variant="h2" fontWeight={800} color="success.main">
                          {comparisonData.detailed_differences_comparison.reduce((sum, row) => sum + (row.score || 0), 0).toFixed(1)}
                        </Typography>
                        <Typography variant="h5" color="text.secondary">/ 80</Typography>
                      </Box>
                      <ScoreDiffChip diff={
                        comparisonData.detailed_differences_comparison.reduce((sum, row) => sum + (row.score || 0), 0) - totalScore
                      } />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {evaluations.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Score Breakdown
                  </Typography>
                  <Grid container spacing={2}>
                    {evaluations.map((evaluation, index) => {
                      const isCounterproductive = evaluation.control_sum_id &&
                        evaluation.control_sum_id.toString().toUpperCase().includes('COUNTERPRODUCTIVE');
                      const max = evaluation.aspect_max_score || 2;
                      const pct = max > 0 ? (evaluation.score / max) * 100 : 0;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: '70%' }}>
                                {evaluation.aspect_name || evaluation.aspect}
                              </Typography>
                              <ScoreChip score={evaluation.score} maxScore={max} />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              color={isCounterproductive ? 'error' : pct >= 85 ? 'success' : pct >= 60 ? 'warning' : 'error'}
                              sx={{ height: 8, borderRadius: 4, mb: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {evaluation.explanation}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {evaluations.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  No evaluation results available yet. The submission may still be processing.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            evaluations.map((evaluation, index) => {
              const isCounterproductive = evaluation.control_sum_id &&
                evaluation.control_sum_id.toString().toUpperCase().includes('COUNTERPRODUCTIVE');
              const gtRow = comparisonData?.detailed_differences_comparison?.find(
                (row) => String(row.control_sum_id) === String(evaluation.control_sum_id)
              );
              const gtScore = gtRow?.score ?? null;
              return (
                <Card key={index} sx={{ borderLeft: 4, borderColor: isCounterproductive ? 'error.main' : 'primary.main' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {evaluation.aspect_name || evaluation.aspect}
                        {isCounterproductive && (
                          <Chip label="Counterproductive" color="error" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScoreChip score={evaluation.score} maxScore={evaluation.aspect_max_score || 2} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Control Sum ID:</strong> {evaluation.control_sum_id}
                      </Typography>
                      {gtScore !== null && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Ground Truth:</strong>{' '}
                          <Typography component="span" fontWeight={700} color="success.main">
                            {gtScore.toFixed(2)}
                          </Typography>
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2">
                      {evaluation.explanation}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          {compareLoading && <Loading message="Loading comparison data..." />}
          {compareError && (
            <ErrorMessage
              title="Comparison not available"
              message={compareError}
              onRetry={fetchComparisonData}
            />
          )}

          {!compareLoading && !compareError && comparisonData && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Score Comparison Summary
                    </Typography>
                    {comparisonData.summary && (
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Total Comparisons</Typography>
                            <Typography variant="h5" fontWeight={700}>{comparisonData.summary.total_comparisons}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Matched Aspects</Typography>
                            <Typography variant="h5" fontWeight={700}>{comparisonData.summary.matched_aspects}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Mean Abs Diff</Typography>
                            <Typography variant="h5" fontWeight={700}>{comparisonData.summary.mean_absolute_difference?.toFixed(2) || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Max Diff</Typography>
                            <Typography variant="h5" fontWeight={700}>{comparisonData.summary.max_absolute_difference?.toFixed(2) || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    {comparisonData.detailed_differences_comparison?.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary">Total Ground Truth</Typography>
                              <Typography variant="h5" fontWeight={700}>
                                {comparisonData.detailed_differences_comparison.reduce((sum, row) => sum + (row.score || 0), 0).toFixed(2)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary">Total Evaluated</Typography>
                              <Typography variant="h5" fontWeight={700} color="primary.main">
                                {comparisonData.detailed_differences_comparison.reduce((sum, row) => sum + (row.llm_score || 0), 0).toFixed(2)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <ScoreDiffChip diff={
                                comparisonData.detailed_differences_comparison.reduce((sum, row) => sum + (row.score || 0), 0) -
                                comparisonData.detailed_differences_comparison.reduce((sum, row) => sum + (row.llm_score || 0), 0)
                              } />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Detailed Comparison
                    </Typography>
                    {comparisonData.detailed_differences_comparison?.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell>Aspect</TableCell>
                              <TableCell align="center">Control Sum</TableCell>
                              <TableCell align="right">Ground Truth</TableCell>
                              <TableCell align="right">Evaluated</TableCell>
                              <TableCell align="center">Difference</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {comparisonData.detailed_differences_comparison.map((row, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                                </TableCell>
                                <TableCell align="center">{row.control_sum_id}</TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={700}>{row.score?.toFixed(2) || '0.00'}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={700} color="primary.main">{row.llm_score?.toFixed(2) || '0.00'}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <ScoreDiffChip diff={row.score - row.llm_score} />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">No comparison data available.</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CandidateEvaluation;
